import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { RefreshCw, DollarSign } from 'lucide-react'
import {
  ServerApiClient,
  type FounderAiSpendResponse,
  type FounderAiSpendFullPrompt,
  type AiSpendRecentCall,
  type AiSpendCounterRow,
} from '../../data/ServerApiClient'
import { ensureApiSessionReady } from '../../data/apiSession'

type Props = {
  topInsetPx?: number
}

type SectionModelFilter = {
  section: string
  modelId: string
}

const panel: CSSProperties = {
  border: '1px solid #d9e2df',
  background: '#fff',
  borderRadius: 8,
  boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
}
const buttonStyle: CSSProperties = {
  border: '1px solid #c8d7d2',
  background: '#fff',
  color: '#15343b',
  borderRadius: 7,
  padding: '7px 10px',
  fontSize: 12,
  fontWeight: 700,
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
}
const inputStyle: CSSProperties = {
  border: '1px solid #c8d7d2',
  borderRadius: 7,
  padding: '7px 9px',
  fontSize: 12,
  color: '#15343b',
  minWidth: 120,
}
const thStyle: CSSProperties = {
  textAlign: 'left',
  fontSize: 11,
  color: '#5d6d72',
  fontWeight: 800,
  borderBottom: '1px solid #e4ece9',
  padding: '8px 10px',
  whiteSpace: 'nowrap',
}
const tdStyle: CSSProperties = {
  fontSize: 12,
  color: '#223236',
  borderBottom: '1px solid #eef3f1',
  padding: '8px 10px',
  verticalAlign: 'top',
}
const numTd: CSSProperties = { ...tdStyle, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }

const fmtInt = (v: number | null | undefined): string => Math.max(0, Math.round(Number(v || 0))).toLocaleString()
const fmtUsd = (v: number | null | undefined): string => `$${(Number(v || 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const fmtUsd4 = (v: number | null | undefined): string => `$${(Number(v || 0)).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })}`
const fmtBytes = (v: number | null | undefined): string => {
  const n = Math.max(0, Number(v || 0))
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${Math.round(n)} B`
}
const fmtBool = (v: boolean | null | undefined): string => (v == null ? '-' : v ? 'yes' : 'no')
const fmtDate = (value: string | null | undefined): string => {
  if (!value) return '-'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}
const short = (value: string | null | undefined, max = 10): string => {
  const s = String(value || '').trim()
  if (!s) return '-'
  return s.length > max ? `${s.slice(0, max)}…` : s
}
const uuidPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i
const isBlankOrUuid = (value: string): boolean => {
  const s = value.trim()
  return !s || uuidPattern.test(s)
}

function MetricCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div style={{ ...panel, padding: 12, display: 'grid', gap: 5, minWidth: 0 }}>
      <div style={{ fontSize: 11, color: '#657478', fontWeight: 800 }}>{label}</div>
      <div style={{ fontSize: 22, color: '#172d33', fontWeight: 850, lineHeight: 1 }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, color: '#7b898d' }}>{sub}</div> : null}
    </div>
  )
}
function TableShell({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ ...panel, overflow: 'auto' }}>
      <div style={{ fontSize: 12, fontWeight: 800, color: '#15343b', padding: '10px 10px 0' }}>{title}</div>
      {children}
    </div>
  )
}

export function FounderAiSpendDashboard({ topInsetPx = 0 }: Props) {
  const api = useMemo(() => new ServerApiClient(), [])
  const loadSeqRef = useRef(0)
  const didInitialLoadRef = useRef(false)
  const [windowDays, setWindowDays] = useState(14)
  const [loadedWindowDays, setLoadedWindowDays] = useState(14)
  const [siteId, setSiteId] = useState('')
  const [data, setData] = useState<FounderAiSpendResponse | null>(null)
  const [selectedCallId, setSelectedCallId] = useState<string | null>(null)
  const [selectedSection, setSelectedSection] = useState<SectionModelFilter | null>(null)
  const [prompt, setPrompt] = useState<FounderAiSpendFullPrompt | null>(null)
  const [promptLoading, setPromptLoading] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async (overrides?: { siteId?: string }) => {
    const effectiveSiteId = overrides?.siteId ?? siteId
    if (!isBlankOrUuid(effectiveSiteId)) {
      setError('Site id must be a UUID.')
      return
    }
    const requestId = loadSeqRef.current + 1
    loadSeqRef.current = requestId
    setLoading(true)
    setError('')
    try {
      const session = await ensureApiSessionReady()
      if (requestId !== loadSeqRef.current) return
      if (!session.token) {
        const next = `${window.location.pathname}${window.location.search}${window.location.hash}`
        window.history.replaceState(window.history.state, '', `/login?next=${encodeURIComponent(next)}`)
        window.dispatchEvent(new Event('fg_route_change'))
        return
      }
      const out = await api.getFounderAiSpend({
        window_days: windowDays,
        site_id: effectiveSiteId.trim() || null,
      })
      if (requestId !== loadSeqRef.current) return
      setData(out)
      setLoadedWindowDays(windowDays)
    } catch (e) {
      if (requestId !== loadSeqRef.current) return
      setError(e instanceof Error ? e.message : 'Failed to load AI spend')
    } finally {
      if (requestId === loadSeqRef.current) setLoading(false)
    }
  }, [api, siteId, windowDays])

  useEffect(() => {
    if (!didInitialLoadRef.current) {
      didInitialLoadRef.current = true
      void load()
      return
    }
    const handle = window.setTimeout(() => void load(), 300)
    return () => window.clearTimeout(handle)
  }, [load])

  const openCallId = useCallback(async (callId: string) => {
    const id = String(callId || '').trim()
    if (!id) return
    if (selectedCallId === id) {
      setSelectedCallId(null)
      setPrompt(null)
      return
    }
    setSelectedCallId(id)
    setPrompt(null)
    setPromptLoading(true)
    try {
      const out = await api.getFounderAiSpendPrompt(id)
      setPrompt(out.fullPrompt)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load prompt')
    } finally {
      setPromptLoading(false)
    }
  }, [api, selectedCallId])

  const report = data?.report
  const labelByWorkbook = useMemo(() => {
    const m = new Map<string, string>()
    for (const l of data?.clientLabels || []) {
      m.set(l.workbookId, l.companyName || l.siteSlug || short(l.workbookId))
    }
    return m
  }, [data])
  const labelByUser = useMemo(() => {
    const m = new Map<string, string>()
    for (const l of data?.userLabels || []) {
      m.set(l.userId, l.fullName || l.email || short(l.userId, 16))
    }
    return m
  }, [data])
  const clientLabel = (workbookId: string | null | undefined): string => {
    const id = String(workbookId || '').trim()
    if (!id) return '(unattributed)'
    return labelByWorkbook.get(id) || short(id)
  }
  const userLabel = (userId: string | null | undefined): string => {
    const id = String(userId || '').trim()
    if (!id) return '(unattributed)'
    return labelByUser.get(id) || short(id, 16)
  }

  const costPerRun = report && report.totalCalls > 0 ? report.totalCostUsd / report.totalCalls : 0
  const caps = data?.caps

  const capPct = (row: AiSpendCounterRow): number | null => {
    if (!row.cap_usd || row.cap_usd <= 0) return null
    return (row.spend_usd / row.cap_usd) * 100
  }
  const visibleCounters = useMemo(() => {
    const counters = data?.counters || []
    if (loadedWindowDays <= 1) return counters.filter((row) => row.period_kind === 'day')
    if (loadedWindowDays < 28) return counters.filter((row) => row.period_kind === 'day')
    return counters
  }, [data?.counters, loadedWindowDays])
  const sectionFilter = String(selectedSection?.section || '').trim()
  const modelFilter = String(selectedSection?.modelId || '').trim()
  const filterLabel = sectionFilter ? `${sectionFilter}${modelFilter ? ` · ${short(modelFilter, 24)}` : ''}` : ''
  const matchesSectionModelFilter = useCallback((row: { section?: string | null; modelId?: string | null }) => {
    if (!sectionFilter) return true
    return String(row.section || '') === sectionFilter && String(row.modelId || '') === modelFilter
  }, [modelFilter, sectionFilter])
  const filteredConversations = useMemo(
    () => (report?.conversations || []).filter(matchesSectionModelFilter),
    [matchesSectionModelFilter, report?.conversations],
  )
  const filteredRecentCalls = useMemo(
    () => (report?.recentCalls || []).filter(matchesSectionModelFilter),
    [matchesSectionModelFilter, report?.recentCalls],
  )
  const firstLayerToolCalls = useMemo(
    () => filteredRecentCalls.filter((call) => Number(call.firstLayerToolCount || 0) > 0),
    [filteredRecentCalls],
  )
  const packetDiagnosticCalls = useMemo(
    () => filteredRecentCalls.filter((call) => {
      const status = String(call.enrichmentPacketStatus || '').trim()
      const slow = Number(call.enrichmentPacketBuildLatencyMs || 0) > 1500
      return Boolean(status && call.enrichmentPacketHit !== true) || slow || call.enrichmentFallbackAgentUsed === true
    }),
    [filteredRecentCalls],
  )
  const contextDiagnosticCalls = useMemo(
    () => filteredRecentCalls.filter((call) => {
      const status = String(call.contextPacketStatus || '').trim()
      const slow = Number(call.contextPacketLookupMs || 0) > 300 || Number(call.contextPacketBuildMs || 0) > 1500
      return call.contextRejectedByCap === true || Boolean(status && call.contextPacketHit !== true) || slow || call.usedBroadTool === true || call.usedColdArtifact === true || call.usedFallbackAgent === true
    }),
    [filteredRecentCalls],
  )
  const contextCapRejectedCalls = useMemo(
    () => filteredRecentCalls.filter((call) => call.contextRejectedByCap === true || call.status === 'rejected_context_cap'),
    [filteredRecentCalls],
  )
  const slowContextInsightCalls = useMemo(
    () => filteredRecentCalls.filter((call) => {
      const hasContextPacket = Boolean(String(call.contextPacketStatus || call.contextPacketKey || '').trim())
      return hasContextPacket && Number(call.latencyMs || 0) > 3000
    }),
    [filteredRecentCalls],
  )
  const clearSectionFilter = () => {
    setSelectedSection(null)
    setSelectedCallId(null)
    setPrompt(null)
  }

  return (
    <div style={{ padding: 16, paddingTop: topInsetPx + 16, display: 'grid', gap: 14, maxWidth: 1200, margin: '0 auto' }}>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <DollarSign size={16} color="#15343b" />
        <strong style={{ fontSize: 14, color: '#15343b' }}>AI spend</strong>
        <span style={{ flex: 1 }} />
        <label style={{ fontSize: 11, color: '#657478', fontWeight: 700 }}>Window (days)</label>
        <input
          style={inputStyle}
          type="number"
          min={1}
          max={365}
          value={windowDays}
          onChange={(e) => setWindowDays(Math.max(1, Math.min(365, Number(e.target.value) || 14)))}
        />
        <input
          style={inputStyle}
          placeholder="Site id (UUID, optional)"
          value={siteId}
          onChange={(e) => setSiteId(e.target.value)}
        />
        <button style={buttonStyle} onClick={() => void load()} disabled={loading}>
          <RefreshCw size={14} /> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>
      {sectionFilter ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#657478' }}>Showing section + model</span>
          <span style={{ border: '1px solid #c8d7d2', borderRadius: 7, padding: '5px 8px', fontSize: 12, fontWeight: 800, color: '#15343b', background: '#f8fbfa' }}>
            {filterLabel}
          </span>
          <button type="button" style={buttonStyle} onClick={clearSectionFilter}>Clear filter</button>
        </div>
      ) : null}

      {error ? (
        <div style={{ ...panel, padding: 10, color: '#9a3412', borderColor: '#fed7aa', background: '#fff7ed', fontSize: 12 }}>{error}</div>
      ) : null}

      {report && !report.configured ? (
        <div style={{ ...panel, padding: 12, fontSize: 12, color: '#657478' }}>
          BigQuery telemetry is not configured for this environment, so per-call AI spend isn’t recorded here.
          Set <code>BQ_DATASET</code> / <code>BQ_STAGING_BUCKET</code> to enable the ledger.
        </div>
      ) : null}

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 }}>
        <MetricCard label="Total cost" value={fmtUsd(report?.totalCostUsd)} sub={`last ${loadedWindowDays}d`} />
        <MetricCard label="Calls" value={fmtInt(report?.totalCalls)} />
        <MetricCard label="Tokens" value={fmtInt(report?.totalTokens)} />
        <MetricCard label="Cost / call" value={fmtUsd4(costPerRun)} />
      </div>

      {/* Spend vs caps */}
      {data && visibleCounters.length > 0 ? (
        <TableShell title={loadedWindowDays < 28 ? 'Spend vs cap (current day)' : 'Spend vs cap (current day + month)'}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Scope</th>
                <th style={thStyle}>Id</th>
                <th style={thStyle}>Period</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Spend</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Cap</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Used</th>
              </tr>
            </thead>
            <tbody>
              {visibleCounters.map((c, i) => {
                const pct = capPct(c)
                const near = pct != null && pct >= 80
                const over = pct != null && pct >= 100
                return (
                  <tr key={`${c.scope_kind}:${c.scope_id}:${c.period_kind}:${i}`}>
                    <td style={tdStyle}>{c.scope_kind}</td>
                    <td style={tdStyle}>{c.scope_kind === 'workbook' ? clientLabel(c.scope_id) : short(c.scope_id, 16)}</td>
                    <td style={tdStyle}>{c.period_kind} {c.period_key}</td>
                    <td style={numTd}>{fmtUsd(c.spend_usd)}</td>
                    <td style={numTd}>{c.cap_usd ? fmtUsd(c.cap_usd) : '—'}</td>
                    <td style={{ ...numTd, color: over ? '#b91c1c' : near ? '#b45309' : '#223236', fontWeight: near ? 800 : 500 }}>
                      {pct == null ? '—' : `${pct.toFixed(0)}%`}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </TableShell>
      ) : null}
      {caps && !caps.userDaily && !caps.userMonthly && !caps.workbookDaily && !caps.workbookMonthly ? (
        <div style={{ fontSize: 11, color: '#7b898d' }}>No spend caps configured (AI_USER_DAILY_CAP_USD, AI_WORKBOOK_DAILY_CAP_USD, …).</div>
      ) : null}

      {/* Per client, user, and section */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12 }}>
        <TableShell title="By client">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Client</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Cost</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Calls</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Tokens</th>
              </tr>
            </thead>
            <tbody>
              {(report?.perClient || []).map((r, i) => (
                <tr key={`${r.workbookId || 'none'}:${r.connectionId || 'none'}:${r.costUsd}:${r.calls}:${i}`}>
                  <td style={tdStyle}>{clientLabel(r.workbookId)}</td>
                  <td style={numTd}>{fmtUsd(r.costUsd)}</td>
                  <td style={numTd}>{fmtInt(r.calls)}</td>
                  <td style={numTd}>{fmtInt(r.tokens)}</td>
                </tr>
              ))}
              {!report?.perClient?.length ? <tr><td style={tdStyle} colSpan={4}>No data.</td></tr> : null}
            </tbody>
          </table>
        </TableShell>

        <TableShell title="By user">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>User</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Cost</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Calls</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Tokens</th>
              </tr>
            </thead>
            <tbody>
              {(report?.perUser || []).map((r, i) => (
                <tr key={`${r.userId || 'none'}:${r.costUsd}:${r.calls}:${i}`}>
                  <td style={tdStyle}>{userLabel(r.userId)}</td>
                  <td style={numTd}>{fmtUsd(r.costUsd)}</td>
                  <td style={numTd}>{fmtInt(r.calls)}</td>
                  <td style={numTd}>{fmtInt(r.tokens)}</td>
                </tr>
              ))}
              {!report?.perUser?.length ? <tr><td style={tdStyle} colSpan={4}>No data.</td></tr> : null}
            </tbody>
          </table>
        </TableShell>

        <TableShell title="By section">
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Section</th>
                <th style={thStyle}>Model</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Cost</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Calls</th>
              </tr>
            </thead>
            <tbody>
              {(report?.perSection || []).map((r, i) => {
                const active = sectionFilter === r.section && modelFilter === r.modelId
                return (
                  <tr
                    key={`${r.section}:${r.modelId}:${i}`}
                    onClick={() => {
                      setSelectedSection(active ? null : { section: r.section, modelId: r.modelId })
                      setSelectedCallId(null)
                      setPrompt(null)
                    }}
                    style={{ cursor: 'pointer', background: active ? '#f1f7f5' : undefined }}
                    title={`Show only ${r.section || '(none)'} calls on ${r.modelId || '(none)'}`}
                  >
                    <td style={tdStyle}>{r.section || '(none)'}</td>
                    <td style={tdStyle}>{short(r.modelId, 22)}</td>
                    <td style={numTd}>{fmtUsd(r.costUsd)}</td>
                    <td style={numTd}>{fmtInt(r.calls)}</td>
                  </tr>
                )
              })}
              {!report?.perSection?.length ? <tr><td style={tdStyle} colSpan={4}>No data.</td></tr> : null}
            </tbody>
          </table>
        </TableShell>
      </div>

      <TableShell title="First-layer evidence clicks with tools">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>When</th>
              <th style={thStyle}>Card</th>
              <th style={thStyle}>Page</th>
              <th style={thStyle}>Tools</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Context</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Cost</th>
            </tr>
          </thead>
          <tbody>
            {firstLayerToolCalls.map((c) => (
              <tr key={`first-layer:${c.callId}`} onClick={() => void openCallId(c.callId)} style={{ cursor: 'pointer' }}>
                <td style={tdStyle}>{fmtDate(c.createdAt)}</td>
                <td style={tdStyle}>{short(c.instantCardKey, 48)}</td>
                <td style={tdStyle}>{c.instantCardSurface || c.activeSection || '-'}</td>
                <td style={tdStyle}>{c.toolNames || fmtInt(c.firstLayerToolCount)}</td>
                <td style={numTd}>{fmtBytes(c.workspaceContextBytes)}</td>
                <td style={numTd}>{fmtUsd4(c.costUsd)}</td>
              </tr>
            ))}
            {!firstLayerToolCalls.length ? (
              <tr><td style={tdStyle} colSpan={6}>No first-layer instant-card calls scheduled tools in this window.</td></tr>
            ) : null}
          </tbody>
        </table>
      </TableShell>

      <TableShell title="Instant-card packet diagnostics">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>When</th>
              <th style={thStyle}>Card</th>
              <th style={thStyle}>Packet</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Hit</th>
              <th style={thStyle}>Fallback</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Build</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Cost</th>
            </tr>
          </thead>
          <tbody>
            {packetDiagnosticCalls.map((c) => (
              <tr key={`packet:${c.callId}`} onClick={() => void openCallId(c.callId)} style={{ cursor: 'pointer' }}>
                <td style={tdStyle}>{fmtDate(c.createdAt)}</td>
                <td style={tdStyle}>{short(c.instantCardKey || c.instantCardSourceComponent, 42)}</td>
                <td style={tdStyle}>{short(c.enrichmentPacketKey, 34)}</td>
                <td style={tdStyle}>{c.enrichmentPacketStatus || '-'}</td>
                <td style={tdStyle}>{fmtBool(c.enrichmentPacketHit)}</td>
                <td style={tdStyle}>{fmtBool(c.enrichmentFallbackAgentUsed)}</td>
                <td style={numTd}>{fmtInt(c.enrichmentPacketBuildLatencyMs)}ms</td>
                <td style={numTd}>{fmtUsd4(c.costUsd)}</td>
              </tr>
            ))}
            {!packetDiagnosticCalls.length ? (
              <tr><td style={tdStyle} colSpan={8}>No packet misses, slow cold builds, or enrichment fallback model calls in this window.</td></tr>
            ) : null}
          </tbody>
        </table>
      </TableShell>

      <TableShell title="AI context packet diagnostics">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>When</th>
              <th style={thStyle}>Card</th>
              <th style={thStyle}>Context</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}>Hit</th>
              <th style={thStyle}>Flags</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Lookup</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Build</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Bytes</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Cost</th>
            </tr>
          </thead>
          <tbody>
            {contextDiagnosticCalls.map((c) => {
              const flags = [
                c.contextRejectedByCap ? 'cap rejected' : '',
                c.usedBroadTool ? 'broad tool' : '',
                c.usedColdArtifact ? 'cold artifact' : '',
                c.usedFallbackAgent ? 'fallback agent' : '',
              ].filter(Boolean).join(', ')
              return (
                <tr key={`context:${c.callId}`} onClick={() => void openCallId(c.callId)} style={{ cursor: 'pointer' }}>
                  <td style={tdStyle}>{fmtDate(c.createdAt)}</td>
                  <td style={tdStyle}>{short(c.instantCardKey || c.instantCardSourceComponent, 42)}</td>
                  <td style={tdStyle}>{short(c.contextPacketKey, 34)}</td>
                  <td style={tdStyle}>{c.contextPacketStatus || '-'}</td>
                  <td style={tdStyle}>{fmtBool(c.contextPacketHit)}</td>
                  <td style={tdStyle}>{flags || '-'}</td>
                  <td style={numTd}>{fmtInt(c.contextPacketLookupMs)}ms</td>
                  <td style={numTd}>{fmtInt(c.contextPacketBuildMs)}ms</td>
                  <td style={numTd}>{fmtBytes(c.contextPacketBytes)}</td>
                  <td style={numTd}>{fmtUsd4(c.costUsd)}</td>
                </tr>
              )
            })}
            {!contextDiagnosticCalls.length ? (
              <tr><td style={tdStyle} colSpan={10}>No context packet misses, slow builds, broad-tool flags, or fallback agent use in this window.</td></tr>
            ) : null}
          </tbody>
        </table>
      </TableShell>

      <TableShell title="Context cap rejections">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>When</th>
              <th style={thStyle}>Client</th>
              <th style={thStyle}>User</th>
              <th style={thStyle}>Card</th>
              <th style={thStyle}>Context</th>
              <th style={thStyle}>Reason</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Context</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Cap</th>
              <th style={thStyle}>Status</th>
            </tr>
          </thead>
          <tbody>
            {contextCapRejectedCalls.map((c) => (
              <tr key={`context-cap:${c.callId}`} onClick={() => void openCallId(c.callId)} style={{ cursor: 'pointer' }}>
                <td style={tdStyle}>{fmtDate(c.createdAt)}</td>
                <td style={tdStyle}>{clientLabel(c.workbookId)}</td>
                <td style={tdStyle}>{userLabel(c.userId)}</td>
                <td style={tdStyle}>{short(c.instantCardKey || c.instantCardSourceComponent, 36)}</td>
                <td style={tdStyle}>{short(c.contextPacketKey, 34)}</td>
                <td style={tdStyle}>{c.contextRejectionReason || '-'}</td>
                <td style={{ ...numTd, color: '#b91c1c', fontWeight: 800 }}>{fmtBytes(c.workspaceContextBytes)}</td>
                <td style={numTd}>{fmtBytes(c.contextCapBytes)}</td>
                <td style={tdStyle}>{c.status}</td>
              </tr>
            ))}
            {!contextCapRejectedCalls.length ? (
              <tr><td style={tdStyle} colSpan={9}>No first-layer context payloads were rejected by byte caps in this window.</td></tr>
            ) : null}
          </tbody>
        </table>
      </TableShell>

      <TableShell title="Context insight calls over 3 seconds">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>When</th>
              <th style={thStyle}>Client</th>
              <th style={thStyle}>User</th>
              <th style={thStyle}>Card</th>
              <th style={thStyle}>Context</th>
              <th style={thStyle}>Status</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Lookup</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Build</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Bytes</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Total</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Cost</th>
            </tr>
          </thead>
          <tbody>
            {slowContextInsightCalls.map((c) => (
              <tr key={`slow-context:${c.callId}`} onClick={() => void openCallId(c.callId)} style={{ cursor: 'pointer' }}>
                <td style={tdStyle}>{fmtDate(c.createdAt)}</td>
                <td style={tdStyle}>{clientLabel(c.workbookId)}</td>
                <td style={tdStyle}>{userLabel(c.userId)}</td>
                <td style={tdStyle}>{short(c.instantCardKey || c.instantCardSourceComponent, 36)}</td>
                <td style={tdStyle}>{short(c.contextPacketKey, 34)}</td>
                <td style={tdStyle}>{c.contextPacketStatus || '-'}</td>
                <td style={numTd}>{fmtInt(c.contextPacketLookupMs)}ms</td>
                <td style={numTd}>{fmtInt(c.contextPacketBuildMs)}ms</td>
                <td style={numTd}>{fmtBytes(c.contextPacketBytes)}</td>
                <td style={{ ...numTd, color: Number(c.latencyMs || 0) > 3000 ? '#b45309' : '#223236', fontWeight: 800 }}>{fmtInt(c.latencyMs)}ms</td>
                <td style={numTd}>{fmtUsd4(c.costUsd)}</td>
              </tr>
            ))}
            {!slowContextInsightCalls.length ? (
              <tr><td style={tdStyle} colSpan={11}>No context-backed insight calls exceeded 3 seconds in this window.</td></tr>
            ) : null}
          </tbody>
        </table>
      </TableShell>

      {/* Conversation rollups */}
      <TableShell title="Conversations / questions (click a row for the latest prompt + output)">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>Question</th>
              <th style={thStyle}>Client</th>
              <th style={thStyle}>User</th>
	              <th style={thStyle}>Page</th>
	              <th style={thStyle}>Card</th>
	              <th style={thStyle}>Model</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Cost</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Calls</th>
	              <th style={{ ...thStyle, textAlign: 'right' }}>Tokens</th>
	              <th style={{ ...thStyle, textAlign: 'right' }}>Context</th>
	              <th style={thStyle}>Last</th>
            </tr>
          </thead>
          <tbody>
            {filteredConversations.map((c) => {
              const open = selectedCallId === c.sampleCallId
              return (
                <Fragment key={`${c.conversationId || c.sampleCallId}:${c.modelId}`}>
                  <tr onClick={() => void openCallId(c.sampleCallId)} style={{ cursor: 'pointer', background: open ? '#f1f7f5' : undefined }}>
                    <td style={{ ...tdStyle, maxWidth: 440 }}>
                      <div style={{ fontWeight: 750, color: '#15343b' }}>{c.question || '(question unavailable)'}</div>
                      <div style={{ marginTop: 3, fontSize: 11, color: '#657478' }}>
                        {c.metricLabel ? `${c.metricLabel} · ` : ''}{c.section}
                      </div>
                    </td>
                    <td style={tdStyle}>{clientLabel(c.workbookId)}</td>
	                    <td style={tdStyle}>{userLabel(c.userId)}</td>
	                    <td style={tdStyle}>{c.activeSection || '-'}</td>
	                    <td style={tdStyle}>{short(c.instantCardKey || c.instantCardSourceComponent, 28)}</td>
	                    <td style={tdStyle}>{short(c.modelId, 20)}</td>
                    <td style={numTd}>{fmtUsd4(c.costUsd)}</td>
                    <td style={numTd}>{fmtInt(c.calls)}</td>
	                    <td style={numTd}>{fmtInt(c.tokens)}</td>
	                    <td style={numTd}>{fmtBytes(c.workspaceContextBytes)}</td>
	                    <td style={tdStyle}>{fmtDate(c.lastAt)}</td>
                  </tr>
                  {open ? (
                    <tr>
	                      <td style={{ ...tdStyle, background: '#f8fbfa' }} colSpan={11}>
                        <PromptDetail loading={promptLoading} prompt={prompt} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              )
            })}
            {!filteredConversations.length ? (
	              <tr><td style={tdStyle} colSpan={11}>{sectionFilter ? `No parsed conversations for ${filterLabel}.` : 'No copilot conversations with parsed questions.'}</td></tr>
            ) : null}
          </tbody>
        </table>
      </TableShell>

      {/* Recent calls with drill-down */}
      <TableShell title="Recent calls (click a row for the full prompt + cost)">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle}>When</th>
              <th style={thStyle}>Section</th>
              <th style={thStyle}>Client</th>
              <th style={thStyle}>User</th>
	              <th style={thStyle}>Model</th>
	              <th style={thStyle}>Card</th>
	              <th style={{ ...thStyle, textAlign: 'right' }}>Cost</th>
	              <th style={{ ...thStyle, textAlign: 'right' }}>Tokens</th>
	              <th style={{ ...thStyle, textAlign: 'right' }}>Context</th>
	              <th style={{ ...thStyle, textAlign: 'right' }}>Latency</th>
              <th style={thStyle}>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecentCalls.map((c) => {
              const open = selectedCallId === c.callId
              return (
                <Fragment key={c.callId}>
                  <tr onClick={() => void openCallId(c.callId)} style={{ cursor: 'pointer', background: open ? '#f1f7f5' : undefined }}>
                    <td style={tdStyle}>{fmtDate(c.createdAt)}</td>
                    <td style={{ ...tdStyle, maxWidth: 340 }}>
                      <div>{c.section}{c.label ? ` · ${c.label}` : ''}</div>
                      {c.question ? <div style={{ marginTop: 3, fontSize: 11, color: '#657478' }}>{c.question}</div> : null}
                    </td>
                    <td style={tdStyle}>{clientLabel(c.workbookId)}</td>
	                    <td style={tdStyle}>{userLabel(c.userId)}</td>
	                    <td style={tdStyle}>{short(c.modelId, 20)}</td>
	                    <td style={tdStyle}>{short(c.instantCardKey || c.instantCardSourceComponent, 28)}</td>
	                    <td style={numTd}>{fmtUsd4(c.costUsd)}</td>
	                    <td style={numTd}>{fmtInt(c.tokens)}</td>
	                    <td style={numTd}>{fmtBytes(c.workspaceContextBytes)}</td>
	                    <td style={numTd}>{fmtInt(c.latencyMs)}ms</td>
                    <td style={{ ...tdStyle, color: c.status?.startsWith('error') ? '#b91c1c' : '#223236' }}>
                      {c.status}{c.stopReason ? ` (${c.stopReason})` : ''}
                    </td>
                  </tr>
                  {open ? (
                    <tr>
	                      <td style={{ ...tdStyle, background: '#f8fbfa' }} colSpan={11}>
                        <PromptDetail loading={promptLoading} prompt={prompt} />
                      </td>
                    </tr>
                  ) : null}
                </Fragment>
              )
            })}
            {!filteredRecentCalls.length ? (
	              <tr><td style={tdStyle} colSpan={11}>{sectionFilter ? `No recent calls for ${filterLabel}.` : 'No calls.'}</td></tr>
            ) : null}
          </tbody>
        </table>
      </TableShell>
    </div>
  )
}

function PromptDetail({ loading, prompt }: { loading: boolean; prompt: FounderAiSpendFullPrompt | null }) {
  if (loading) return <div style={{ fontSize: 12, color: '#657478' }}>Loading prompt…</div>
  if (!prompt) return <div style={{ fontSize: 12, color: '#657478' }}>No prompt payload.</div>
  return (
    <div style={{ display: 'grid', gap: 8 }}>
      <div style={{ fontSize: 11, color: '#657478' }}>
        {prompt.modelId} · {prompt.section}{prompt.truncated ? ' · (inline, GCS blob unavailable)' : ''}
      </div>
      <PromptBlock title="System" text={prompt.systemPrompt} />
      <PromptBlock title="User" text={prompt.userPrompt} />
      <PromptBlock title="Output" text={prompt.outputText} />
    </div>
  )
}

function PromptBlock({ title, text }: { title: string; text: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#5d6d72', marginBottom: 3 }}>{title}</div>
      <pre style={{
        margin: 0,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
        fontSize: 11,
        lineHeight: 1.45,
        color: '#223236',
        background: '#fff',
        border: '1px solid #e4ece9',
        borderRadius: 6,
        padding: 8,
        maxHeight: 280,
        overflow: 'auto',
      }}>{text || '(empty)'}</pre>
    </div>
  )
}
